/**
 * usePTT Hook
 *
 * Manages Push-to-Talk recording with Speech-to-Text transcription.
 */

import { useState, useCallback, useRef } from 'react'
import type { SpeechToTextResult } from '@/lib/types'
import {
  startRecording,
  stopRecording,
  cancelRecording,
  isRecording as checkIsRecording,
  requestMicrophonePermission,
} from '@/lib/audio/recorder'
import {
  startSpeechRecognition,
  stopSpeechRecognition,
  abortSpeechRecognition,
  isSpeechRecognitionAvailable,
} from '@/lib/audio/speech-to-text'
import { blobToUint8Array } from '@/lib/audio/codec'

export interface UsePTTOptions {
  maxDuration?: number
  enableTranscription?: boolean
  language?: string
  onRecordingStart?: () => void
  onRecordingEnd?: (result: PTTResult | null) => void
  onTranscriptUpdate?: (transcript: string, isFinal: boolean) => void
  onError?: (error: Error) => void
}

export interface PTTResult {
  audioBlob: Uint8Array
  duration: number
  transcript?: string
  transcriptConfidence?: number
}

export interface UsePTTReturn {
  isRecording: boolean
  recordingDuration: number
  currentTranscript: string
  hasPermission: boolean
  isTranscriptionAvailable: boolean
  startPTT: () => Promise<void>
  stopPTT: () => Promise<PTTResult | null>
  cancelPTT: () => void
  requestPermission: () => Promise<boolean>
}

const MAX_DURATION_DEFAULT = 30 * 1000 // 30 seconds

export function usePTT(options: UsePTTOptions = {}): UsePTTReturn {
  const {
    maxDuration = MAX_DURATION_DEFAULT,
    enableTranscription = true,
    language = 'en-US',
    onRecordingStart,
    onRecordingEnd,
    onTranscriptUpdate,
    onError,
  } = options

  const [isRecording, setIsRecording] = useState(false)
  const [recordingDuration, setRecordingDuration] = useState(0)
  const [currentTranscript, setCurrentTranscript] = useState('')
  const [hasPermission, setHasPermission] = useState(false)

  const transcriptRef = useRef('')
  const confidenceRef = useRef(0)

  const isTranscriptionAvailable = isSpeechRecognitionAvailable()

  const handleTranscriptResult = useCallback(
    (result: SpeechToTextResult) => {
      transcriptRef.current = result.transcript
      confidenceRef.current = result.confidence
      setCurrentTranscript(result.transcript)
      onTranscriptUpdate?.(result.transcript, result.isFinal)
    },
    [onTranscriptUpdate]
  )

  const requestPermission = useCallback(async (): Promise<boolean> => {
    const granted = await requestMicrophonePermission()
    setHasPermission(granted)
    return granted
  }, [])

  const startPTT = useCallback(async () => {
    if (isRecording) return

    // Reset state
    setRecordingDuration(0)
    setCurrentTranscript('')
    transcriptRef.current = ''
    confidenceRef.current = 0

    try {
      // Start audio recording
      await startRecording({
        maxDuration,
        onTimeUpdate: (elapsed) => {
          setRecordingDuration(elapsed)
        },
        onError: (err) => {
          onError?.(err)
          setIsRecording(false)
        },
      })

      setIsRecording(true)
      onRecordingStart?.()

      // Start speech recognition if enabled and available
      if (enableTranscription && isTranscriptionAvailable) {
        startSpeechRecognition({
          language,
          onInterimResult: handleTranscriptResult,
          onFinalResult: handleTranscriptResult,
          onError: (err) => {
            console.warn('[PTT] Speech recognition error:', err)
            // Don't fail recording if STT fails
          },
        })
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      onError?.(err)
      setIsRecording(false)
    }
  }, [
    isRecording,
    maxDuration,
    enableTranscription,
    isTranscriptionAvailable,
    language,
    handleTranscriptResult,
    onRecordingStart,
    onError,
  ])

  const stopPTT = useCallback(async (): Promise<PTTResult | null> => {
    if (!checkIsRecording()) {
      setIsRecording(false)
      return null
    }

    // Stop speech recognition first and capture the final transcript
    let sttResult = null
    if (enableTranscription && isTranscriptionAvailable) {
      sttResult = stopSpeechRecognition()
    }

    // Stop audio recording
    const recordingResult = await stopRecording()
    setIsRecording(false)

    if (!recordingResult) {
      onRecordingEnd?.(null)
      return null
    }

    // Convert blob to Uint8Array
    const audioBlob = await blobToUint8Array(recordingResult.blob)

    // Use transcript from stopSpeechRecognition result if ref is empty
    // This handles the case where final result callback hasn't fired yet
    const transcript = transcriptRef.current || sttResult?.transcript || undefined
    const confidence = confidenceRef.current || sttResult?.confidence || undefined

    const result: PTTResult = {
      audioBlob,
      duration: recordingResult.duration,
      transcript,
      transcriptConfidence: confidence,
    }

    onRecordingEnd?.(result)
    return result
  }, [enableTranscription, isTranscriptionAvailable, onRecordingEnd])

  const cancelPTT = useCallback(() => {
    if (enableTranscription && isTranscriptionAvailable) {
      abortSpeechRecognition()
    }
    cancelRecording()
    setIsRecording(false)
    setRecordingDuration(0)
    setCurrentTranscript('')
  }, [enableTranscription, isTranscriptionAvailable])

  return {
    isRecording,
    recordingDuration,
    currentTranscript,
    hasPermission,
    isTranscriptionAvailable,
    startPTT,
    stopPTT,
    cancelPTT,
    requestPermission,
  }
}
